import cx from "classnames";
import { push } from "react-router-redux";
import { t } from "ttag";

import { DateTime } from "metabase/common/components/DateTime";
import { SortableColumnHeader } from "metabase/common/components/ItemsTable/BaseItemsTable";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import AdminS from "metabase/css/admin.module.css";
import CS from "metabase/css/core/index.css";
import { renderMetabotProfileLabel } from "metabase/metabot/constants";
import { useDispatch } from "metabase/redux";
import { Badge, Ellipsified, Flex, Tooltip } from "metabase/ui";
import { EMPTY_CELL_PLACEHOLDER } from "metabase/utils/constants";
import { formatNumber } from "metabase/utils/formatting";
import { getUserName } from "metabase/utils/user";
import type { SortingOptions } from "metabase-types/api";

import type { ConversationSortColumn, ConversationSummary } from "../../types";

import S from "./ConversationsTable.module.css";

type Props = {
  conversations: ConversationSummary[];
  isLoading: boolean;
  error: unknown;
  sortingOptions: SortingOptions<ConversationSortColumn>;
  onSortingOptionsChange: (
    options: SortingOptions<ConversationSortColumn>,
  ) => void;
};

export function ConversationsTable({
  conversations,
  isLoading,
  error,
  sortingOptions,
  onSortingOptionsChange,
}: Props) {
  const dispatch = useDispatch();
  const showLoadingAndError = isLoading || error != null;

  const handleRowClick = (convo: ConversationSummary) => {
    dispatch(
      push(
        `/admin/metabot/usage-auditing/conversations/${convo.conversation_id}`,
      ),
    );
  };

  return (
    <table className={cx(AdminS.ContentTable, S.table)}>
      <thead>
        <tr>
          <SortableColumnHeader
            name="user"
            sortingOptions={sortingOptions}
            onSortingOptionsChange={onSortingOptionsChange}
          >{t`User`}</SortableColumnHeader>
          <SortableColumnHeader
            name="profile_id"
            sortingOptions={sortingOptions}
            onSortingOptionsChange={onSortingOptionsChange}
          >{t`Profile`}</SortableColumnHeader>
          <SortableColumnHeader
            name="created_at"
            sortingOptions={sortingOptions}
            onSortingOptionsChange={onSortingOptionsChange}
          >{t`Date`}</SortableColumnHeader>
          <SortableColumnHeader
            name="message_count"
            sortingOptions={sortingOptions}
            onSortingOptionsChange={onSortingOptionsChange}
            columnHeaderProps={{ style: { width: "5rem" } }}
          >{t`Messages`}</SortableColumnHeader>
          <SortableColumnHeader
            name="total_tokens"
            sortingOptions={sortingOptions}
            onSortingOptionsChange={onSortingOptionsChange}
          >{t`Tokens`}</SortableColumnHeader>
          <SortableColumnHeader
            name="cache_read_tokens"
            sortingOptions={sortingOptions}
            onSortingOptionsChange={onSortingOptionsChange}
          >
            <Tooltip
              label={t`Portion of tokens served from the provider cache. A subset of Tokens, not an additional count.`}
            >
              <span>{t`Cached tokens`}</span>
            </Tooltip>
          </SortableColumnHeader>
          <th>{t`Queries`}</th>
          <th>{t`Searches`}</th>
          <SortableColumnHeader
            name="ip_address"
            sortingOptions={sortingOptions}
            onSortingOptionsChange={onSortingOptionsChange}
          >{t`IP`}</SortableColumnHeader>
        </tr>
      </thead>
      <tbody>
        {showLoadingAndError && (
          <tr>
            <td colSpan={9}>
              <LoadingAndErrorWrapper loading={isLoading} error={error} />
            </td>
          </tr>
        )}

        {!showLoadingAndError && (
          <>
            {conversations.length === 0 && (
              <tr>
                <td colSpan={9}>
                  <Flex c="text-disabled" justify="center">
                    {t`No conversations found`}
                  </Flex>
                </td>
              </tr>
            )}

            {conversations.map((convo) => (
              <tr
                key={convo.conversation_id}
                className={CS.cursorPointer}
                onClick={() => handleRowClick(convo)}
              >
                <td>{convo.user ? getUserName(convo.user) : t`Unknown`}</td>
                <td>
                  {convo.profile_id && (
                    <Badge size="sm" variant="light">
                      {renderMetabotProfileLabel(convo.profile_id)}
                    </Badge>
                  )}
                </td>
                <td>
                  <Ellipsified style={{ maxWidth: 180 }}>
                    <DateTime value={convo.created_at} unit="day" />
                  </Ellipsified>
                </td>
                <td>{formatNumber(convo.message_count)}</td>
                <td>{formatNumber(convo.total_tokens)}</td>
                <td>{formatNumber(convo.cache_read_tokens)}</td>
                <td>{formatNumber(convo.query_count)}</td>
                <td>{formatNumber(convo.search_count)}</td>
                <td>{convo.ip_address ?? EMPTY_CELL_PLACEHOLDER}</td>
              </tr>
            ))}
          </>
        )}
      </tbody>
    </table>
  );
}
